
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Author {
  id: string;
  name: string;
  location: string;
  bio: string;
  recent_work: string;
  quote: string;
  achievements: string[];
  image_url: string;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthorEditorProps {
  author: Author | null;
  onSave: () => void;
  onCancel: () => void;
}

export default function AuthorEditor({ author, onSave, onCancel }: AuthorEditorProps) {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [recentWork, setRecentWork] = useState('');
  const [quote, setQuote] = useState('');
  const [achievements, setAchievements] = useState<string[]>(['']);
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (author) {
      setName(author.name);
      setLocation(author.location || '');
      setBio(author.bio || '');
      setRecentWork(author.recent_work || '');
      setQuote(author.quote || '');
      setAchievements(author.achievements && author.achievements.length > 0 ? author.achievements : ['']);
      setImageUrl(author.image_url || '');
    }
  }, [author]);

  const handleAchievementChange = (index: number, value: string) => {
    const newAchievements = [...achievements];
    newAchievements[index] = value;
    setAchievements(newAchievements);
  };

  const addAchievement = () => {
    setAchievements([...achievements, '']);
  };

  const removeAchievement = (index: number) => {
    if (achievements.length > 1) {
      const newAchievements = achievements.filter((_, i) => i !== index);
      setAchievements(newAchievements);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Filter out empty achievements
      const filteredAchievements = achievements.filter(achievement => achievement.trim() !== '');

      const authorData = {
        name,
        location,
        bio,
        recent_work: recentWork,
        quote,
        achievements: filteredAchievements,
        image_url: imageUrl,
        is_featured: true,
        updated_at: new Date().toISOString(),
      };

      if (author) {
        // Update existing author
        const { error } = await supabase
          .from('authors')
          .update(authorData)
          .eq('id', author.id);

        if (error) {
          setError(error.message);
        } else {
          onSave();
        }
      } else {
        // First, set all authors as not featured
        await supabase
          .from('authors')
          .update({ is_featured: false })
          .neq('id', '00000000-0000-0000-0000-000000000000');

        // Create new featured author
        const { error } = await supabase
          .from('authors')
          .insert([authorData]);

        if (error) {
          setError(error.message);
        } else {
          onSave();
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Edit Featured Author
        </h3>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Author Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                Location
              </label>
              <input
                type="text"
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
          </div>

          <div>
            <label htmlFor="recent-work" className="block text-sm font-medium text-gray-700">
              Recent Work
            </label>
            <input
              type="text"
              id="recent-work"
              value={recentWork}
              onChange={(e) => setRecentWork(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="image-url" className="block text-sm font-medium text-gray-700">
              Author Photo URL
            </label>
            <input
              type="url"
              id="image-url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/author-photo.jpg"
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
            {imageUrl && (
              <div className="mt-3">
                <p className="text-sm text-gray-600 mb-2">Photo Preview:</p>
                <img
                  src={imageUrl}
                  alt="Author preview"
                  className="w-32 h-40 object-cover object-top rounded-lg shadow-md"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          <div>
            <label htmlFor="quote" className="block text-sm font-medium text-gray-700">
              Quote
            </label>
            <textarea
              id="quote"
              rows={3}
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="A memorable quote from the author..."
            />
          </div>

          <div>
            <label htmlFor="bio" className="block text-sm font-medium text-gray-700">
              Biography
            </label>
            <textarea
              id="bio"
              rows={6}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Author's biography and background..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Achievements
            </label>
            <div className="space-y-3">
              {achievements.map((achievement, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <input
                    type="text"
                    value={achievement}
                    onChange={(e) => handleAchievementChange(index, e.target.value)}
                    placeholder="Enter an achievement..."
                    className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  {achievements.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeAchievement(index)}
                      className="w-8 h-8 flex items-center justify-center text-red-600 hover:text-red-800 cursor-pointer"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addAchievement}
                className="flex items-center text-sm text-indigo-600 hover:text-indigo-800 cursor-pointer"
              >
                <i className="ri-add-line mr-1"></i>
                Add Achievement
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 whitespace-nowrap cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {loading ? 'Saving...' : 'Save Author'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
